import Multiselect from "multiselect-react-dropdown";
import React from "react";
import { Spinner, Table } from "react-bootstrap";
import { ensureArray } from "../../../Helpers/utils";
import { MultiSelectModules } from "../helpers/aclHelper";
import PermissionButton from "./PermissionButton";

const SELECT_ALL = { name: "Select All", id: "__all__" };

const style = {
    searchBox: {
        border: '1px solid #ced4da',
        borderRadius: '4px',
        minHeight: '38px',
        maxHeight: '43px',
        padding: '5px 14px',
        overflowY: 'auto',
        overflowX: 'hidden'
    },
    option: {
        color: 'black',
        background: 'white'
    },
    optionContainer: {
        borderRadius: '4px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        maxHeight: '250px',
        overflowY: 'auto'
    }
};

const DEPENDENCIES = {
    "Manage CLI": "Manage Dialers",
    "Manage API CLI": "Manage Dialers"
};

const AclTable = ({
    loading,
    filteredModules,
    permissions,
    expandedModules,
    moduleListingData,
    multiSelectOptions,
    togglePermission,
    toggleAll,
    isAllChecked,
    toggleExpand,
    setModuleListingData
}) => {

    const isDependencyMet = (moduleName) => {
        const parent = DEPENDENCIES[moduleName];
        if (!parent) return true;

        const hasPermission = permissions?.[parent]?.view_permission === true;
        const hasListingData =
            Array.isArray(moduleListingData?.[parent]) &&
            moduleListingData[parent].length > 0;

        return hasPermission && hasListingData;
    };

    const getOptions = (moduleName) => {
        let original = ensureArray(multiSelectOptions?.[moduleName]);

        const selectedDialers = ensureArray(
            moduleListingData?.["Manage Dialers"]
        ).map(r => r.id ?? r);
        if (
            moduleName === "Manage CLI" ||
            moduleName === "Manage API CLI"
        ) {
            const selectedDialersData = ensureArray(
                multiSelectOptions?.["Manage Dialers"]
            ).filter(r => selectedDialers.includes(r.id));
            const selectedDialerNames = selectedDialersData.map(
                d => d.dialerName || d.name
            );

            original = original.filter(option =>
                selectedDialerNames.includes(
                    option.dialerName || option.parentDialerName
                )
            );
        }

        return [SELECT_ALL, ...original];
    };

    const getSelectedValues = (moduleName) => {
        const selectedIds = ensureArray(moduleListingData[moduleName]).map(r => r?.id ?? r);
        const allOptions = ensureArray(multiSelectOptions[moduleName]);

        const selectedObjects = allOptions.filter(opt =>
            selectedIds.includes(opt.id)
        );

        const allSelected =
            selectedObjects.length === allOptions.length &&
            allOptions.length > 0;

        return allSelected
            ? [SELECT_ALL, ...allOptions]
            : selectedObjects;
    };

    const handleSelect = (moduleName, selectedList, selectedItem) => {
        if (!isDependencyMet(moduleName)) return;

        const allItems = ensureArray(multiSelectOptions[moduleName]);

        if (selectedItem.id === "__all__") {
            setModuleListingData(prev => ({
                ...prev,
                [moduleName]: allItems
            }));
            return;
        }

        const cleaned = selectedList.filter(i => i.id !== "__all__");

        setModuleListingData(prev => ({
            ...prev,
            [moduleName]: cleaned
        }));
    };

    const handleRemove = (moduleName, selectedList, removedItem) => {
        console.log({ removedItem })
        if (removedItem.id === "__all__") {
            setModuleListingData(prev => ({
                ...prev,
                [moduleName]: [],
                ...(moduleName === "Manage Dialers" && {
                    "Manage CLI": [],
                    "Manage API CLI": []
                })
            }));
            return;
        }

        const cleaned = selectedList.filter(i => i.id !== "__all__");
        if (moduleName === "Manage Dialers") {

            const manageCliData = multiSelectOptions["Manage CLI"].filter(d => d.dialerName === removedItem.name).map(d => d.id)
            const manageApiCliData = multiSelectOptions["Manage API CLI"].filter(d => d.dialerName === removedItem.name).map(d => d.id)


            setModuleListingData(prev => ({

                ...prev,

                [moduleName]: cleaned,

                // Remove unrelated CLI
                "Manage CLI": ensureArray(prev["Manage CLI"]).filter(cli =>
                    !manageCliData.includes(
                        cli?.id ?? cli
                    )
                ),

                // Remove unrelated API CLI
                "Manage API CLI": ensureArray(prev["Manage API CLI"]).filter(api =>
                    !manageApiCliData.includes(
                        api?.id ?? api
                    )
                )
            }));

            return;
        }

        setModuleListingData(prev => ({
            ...prev,
            [moduleName]: cleaned
        }));
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <div>Loading modules...</div>
            </div>
        );
    }

    return (
        <Table bordered className="align-middle">
            <thead className="table-light">
                <tr>
                    <th>Module Name</th>
                    <th className="text-center">Read</th>
                    <th className="text-center">Add</th>
                    <th className="text-center">Edit</th>
                    <th className="text-center">Delete</th>
                    <th className="text-center">View Permission</th>
                    <th className="text-center">All</th>
                </tr>
            </thead>

            <tbody>
                {filteredModules.map(m => {
                    const hasChild = m.children?.length > 0;
                    const p = permissions[m.mname] || {};

                    const parentKeys = hasChild
                        ? ['read', 'view_permission']
                        : ['read', 'add', 'edit', 'delete', 'view_permission'];

                    const disabled = !isDependencyMet(m.mname);

                    return (
                        <React.Fragment key={m.mid}>

                            {/* PARENT ROW */}
                            <tr>
                                <td className="fw-bold">
                                    {hasChild && (
                                        <span
                                            style={{ cursor: 'pointer', marginRight: 8 }}
                                            onClick={() => toggleExpand(m.mname)}
                                        >
                                            {expandedModules[m.mname] ? '▼' : '▶'}
                                        </span>
                                    )}
                                    {m.mname}
                                </td>

                                {hasChild ? (
                                    <>
                                        <td className="permission-cell">
                                            <PermissionButton
                                                active={p.read || false}
                                                onClick={() => togglePermission(m.mname, 'read')}
                                            />
                                        </td>

                                        <td colSpan={3} className="text-center">
                                            {MultiSelectModules.has(m.mname) && p.view_permission ? (
                                                <>
                                                    {disabled ?
                                                        <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                                                            Select <b>Manage Dialers</b> first
                                                        </div> :
                                                        <Multiselect
                                                            options={getOptions(m.mname)}
                                                            selectedValues={getSelectedValues(m.mname)}
                                                            displayValue="name"
                                                            onSelect={(list, item) =>
                                                                handleSelect(m.mname, list, item)
                                                            }
                                                            onRemove={(list, item) =>
                                                                handleRemove(m.mname, list, item)
                                                            }
                                                            showCheckbox
                                                            hideSelectedList
                                                            placeholder={
                                                                moduleListingData[m.mname]?.length > 0
                                                                    ? `${moduleListingData[m.mname].length} ${m.mname} Selected`
                                                                    : `Select ${m.mname}`
                                                            }
                                                            style={style}
                                                        />}
                                                </>
                                            ) : (
                                                <div className="text-muted">
                                                    <strong>Only have Read and View Permissions</strong>
                                                </div>
                                            )}
                                        </td>

                                        <td className="permission-cell">
                                            <PermissionButton
                                                active={p.view_permission || false}
                                                onClick={() => togglePermission(m.mname, 'view_permission')}
                                            />
                                        </td>
                                    </>
                                ) : (
                                    parentKeys.map(key => (
                                        <td key={key} className="permission-cell">
                                            <PermissionButton
                                                active={p[key] || false}
                                                onClick={() => togglePermission(m.mname, key)}
                                            />
                                        </td>
                                    ))
                                )}

                                <td className="permission-cell">
                                    <PermissionButton
                                        active={isAllChecked(m.mname, parentKeys)}
                                        onClick={() =>
                                            toggleAll(
                                                m.mname,
                                                parentKeys,
                                                !isAllChecked(m.mname, parentKeys)
                                            )
                                        }
                                    />
                                </td>
                            </tr>

                            {/* CHILDREN */}
                            {hasChild && expandedModules[m.mname] &&
                                m.children.map(c => {
                                    const cp = permissions[c.moduleName] || {};

                                    return (
                                        <tr key={`${m.mid}-${c.moduleName}`}>
                                            <td className="ps-4">{c.moduleName}</td>

                                            {['read', 'add', 'edit', 'delete', 'view_permission'].map(key => (
                                                <td key={key} className="permission-cell">
                                                    <PermissionButton
                                                        active={cp[key] || false}
                                                        onClick={() => togglePermission(c.moduleName, key)}
                                                    />
                                                </td>
                                            ))}

                                            <td className="permission-cell">
                                                <PermissionButton
                                                    active={isAllChecked(
                                                        c.moduleName,
                                                        ['read', 'add', 'edit', 'delete', 'view_permission']
                                                    )}
                                                    onClick={() =>
                                                        toggleAll(
                                                            c.moduleName,
                                                            ['read', 'add', 'edit', 'delete', 'view_permission'],
                                                            !isAllChecked(
                                                                c.moduleName,
                                                                ['read', 'add', 'edit', 'delete', 'view_permission']
                                                            )
                                                        )
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </Table>
    );
};

export default AclTable;